import { RefreshCw } from "lucide-react";

/**
 * SnapshotHistoryStrip
 * --------------------
 * Renders the last-5 signed runway snapshots as horizontally scrollable chips
 * under the Liquidity Runway widget. Each chip surfaces headroom, marg-save,
 * a live countdown to expiry, copy/email counters, and Pin A / Pin B toggles
 * that feed the Runway Comparison Overlay. A Restore button re-opens the
 * Snapshot Preview Drawer with the historical signed URL — no fresh POST.
 */
export function SnapshotHistoryStrip({
  history,
  pinA,
  pinB,
  onPin,
  onClear,
  onOpenComparison,
  onRestore,
  formatCountdown,
}) {
  if (!history || history.length === 0) return null;
  const hasPin = Boolean(pinA || pinB);
  return (
    <div
      data-testid="runway-snapshot-history"
      className="mb-3 pt-2 border-t border-cyan-500/15"
    >
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-[9px] font-mono uppercase tracking-[0.28em] text-slate-500">
          Recent Snapshots · pin two to compare
        </div>
        {hasPin && (
          <div className="flex items-center gap-1.5">
            {pinA && pinB && (
              <button
                onClick={onOpenComparison}
                data-testid="runway-open-comparison"
                className="text-[9px] font-mono uppercase tracking-[0.22em] px-2 py-0.5 rounded"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(16,185,129,0.22), rgba(6,182,212,0.14))",
                  border: "1px solid rgba(16,185,129,0.6)",
                  color: "#6ee7b7",
                  boxShadow: "0 8px 18px -8px rgba(16,185,129,0.55)",
                }}
              >
                Compare A vs B
              </button>
            )}
            <button
              onClick={onClear}
              data-testid="runway-clear-pins"
              className="text-[9px] font-mono uppercase tracking-[0.2em] text-slate-500 hover:text-slate-300 px-1.5"
            >
              clear
            </button>
          </div>
        )}
      </div>
      <div className="flex gap-1.5 overflow-x-auto pb-1 -mx-0.5 px-0.5">
        {history.map((s) => {
          const isA = pinA?.token === s.token;
          const isB = pinB?.token === s.token;
          const pinned = isA || isB;
          const first8 = s.token.slice(0, 8);
          return (
            <div
              key={s.token}
              data-testid={`runway-history-chip-${first8}`}
              data-pinned={pinned ? (isA ? "A" : "B") : "false"}
              className="flex-shrink-0 rounded-md px-2 py-1 text-[9px] font-mono"
              style={{
                minWidth: 148,
                background: pinned
                  ? "linear-gradient(135deg, rgba(16,185,129,0.18), rgba(6,182,212,0.06))"
                  : "linear-gradient(135deg, rgba(30,41,59,0.7), rgba(15,23,42,0.7))",
                border: `1px solid ${pinned ? "rgba(16,185,129,0.55)" : "rgba(51,65,85,0.6)"}`,
                boxShadow: pinned
                  ? "0 8px 16px -8px rgba(16,185,129,0.5)"
                  : "none",
              }}
            >
              <div className="flex items-center justify-between text-slate-100">
                <span className="font-semibold text-white">
                  {Math.round(s.headroom || 0)}
                  <span className="text-slate-500 ml-1">/100</span>
                </span>
                <span className="text-cyan-300/80">
                  {s.symbol || "$"}
                  {Math.round(s.marginal_savings || 0)}
                </span>
              </div>
              <div className="flex items-center justify-between mt-0.5 text-[8px] text-slate-400 uppercase tracking-widest">
                <span data-testid={`runway-history-countdown-${first8}`}>
                  {formatCountdown(s.seconds_left)} left
                </span>
                <span className="text-slate-500">
                  {s.copy_count > 0 && `c${s.copy_count} `}
                  {s.email_count > 0 && `e${s.email_count}`}
                </span>
              </div>
              <div className="flex gap-1 mt-1">
                <button
                  onClick={() => onPin(s, "A")}
                  data-testid={`runway-pin-a-${first8}`}
                  className={`flex-1 text-[8px] uppercase tracking-widest py-0.5 rounded ${isA ? "text-emerald-950" : "text-emerald-300/80 hover:text-emerald-200"}`}
                  style={{
                    background: isA
                      ? "linear-gradient(135deg, #10B981, #06B6D4)"
                      : "rgba(16,185,129,0.08)",
                    border: `1px solid ${isA ? "transparent" : "rgba(16,185,129,0.28)"}`,
                    fontWeight: isA ? 700 : 500,
                  }}
                >
                  {isA ? "A ✓" : "pin A"}
                </button>
                <button
                  onClick={() => onPin(s, "B")}
                  data-testid={`runway-pin-b-${first8}`}
                  className={`flex-1 text-[8px] uppercase tracking-widest py-0.5 rounded ${isB ? "text-cyan-950" : "text-cyan-300/80 hover:text-cyan-200"}`}
                  style={{
                    background: isB
                      ? "linear-gradient(135deg, #22d3ee, #06B6D4)"
                      : "rgba(6,182,212,0.08)",
                    border: `1px solid ${isB ? "transparent" : "rgba(6,182,212,0.28)"}`,
                    fontWeight: isB ? 700 : 500,
                  }}
                >
                  {isB ? "B ✓" : "pin B"}
                </button>
                <button
                  onClick={() => onRestore(s)}
                  data-testid={`runway-restore-${first8}`}
                  aria-label="Reopen preview for this snapshot"
                  title="Restore this snapshot in the preview drawer"
                  className="w-6 flex items-center justify-center rounded text-slate-400 hover:text-cyan-200 hover:bg-cyan-500/10 transition-colors"
                  style={{
                    border: "1px solid rgba(51,65,85,0.5)",
                  }}
                >
                  <RefreshCw size={9} />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
